/**
 * Google Drive Picker integration.
 *
 * Loads Google Identity Services (GIS) + the Picker JS library on first
 * use, walks the admin through an OAuth consent for `drive.readonly`, and
 * resolves with the folder they pick. The OAuth grant is NOT persisted —
 * once the folder ID is captured, the indexer takes over using the
 * service-account's Domain-Wide Delegation auth.
 *
 * No Google libraries are bundled at build time — script tags are appended
 * lazily so unauthenticated visitors don't pull Google JS.
 */

interface PickerConfig {
    apiKey: string;
    clientId: string;
}

interface GapiLoader {
    load(library: 'picker', callback: () => void): void;
}

interface GooglePickerView {
    setSelectFolderEnabled(enabled: boolean): GooglePickerView;
    setMimeTypes(types: string): GooglePickerView;
    setIncludeFolders(include: boolean): GooglePickerView;
    setMode(mode: number): GooglePickerView;
    setEnableDrives(enabled: boolean): GooglePickerView;
}

interface GooglePickerBuilder {
    addView(view: unknown): GooglePickerBuilder;
    setOAuthToken(token: string): GooglePickerBuilder;
    setDeveloperKey(key: string): GooglePickerBuilder;
    setCallback(cb: (data: { action: string; docs?: PickedDoc[] }) => void): GooglePickerBuilder;
    setOrigin(origin: string): GooglePickerBuilder;
    setTitle(title: string): GooglePickerBuilder;
    enableFeature(feature: unknown): GooglePickerBuilder;
    build(): { setVisible(visible: boolean): void };
}

interface GooglePickerNamespace {
    PickerBuilder: new () => GooglePickerBuilder;
    DocsView: new (viewId?: unknown) => GooglePickerView;
    ViewId: { FOLDERS: unknown; DOCS: unknown };
    Action: { PICKED: string; CANCEL: string; ERROR?: string; LOADED?: string };
    DocsViewMode: { LIST: number; GRID: number };
    Feature: { SUPPORT_DRIVES: unknown; MULTISELECT_ENABLED: unknown };
}

interface PickedDoc {
    id: string;
    name: string;
    mimeType: string;
    /** Drive resource ID for the parent (My Drive root, Shared Drive, etc.). */
    parentId?: string;
    /** Display name of the enclosing drive (Shared Drive name, or "My Drive"). */
    driveName?: string;
    /** Web-viewable URL Drive returns; not used but logged for diagnostics. */
    url?: string;
}

declare global {
    interface Window {
        gapi?: GapiLoader;
        google?: { picker: GooglePickerNamespace; accounts?: GoogleAccountsNamespace };
    }
}

interface GoogleAccountsNamespace {
    oauth2: {
        initTokenClient(config: {
            client_id: string;
            scope: string;
            callback: (resp: { access_token?: string; error?: string }) => void;
            error_callback?: (err: { type?: string; message?: string }) => void;
        }): { requestAccessToken: (overrides?: { prompt?: string }) => void };
    };
}

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const GAPI_SRC = 'https://apis.google.com/js/api.js';
const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.readonly';

let _gisPromise: Promise<void> | null = null;
let _pickerPromise: Promise<void> | null = null;

function loadScript(src: string, isAlreadyReady: () => boolean): Promise<void> {
    return new Promise((resolve, reject) => {
        // The script might already be present + loaded from a previous
        // call (or pre-injected by another component). Trust a global
        // readiness probe over `dataset.loaded` so an externally-injected
        // tag without our marker doesn't hang forever waiting for a
        // `load` event that already fired.
        if (isAlreadyReady()) return resolve();
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
        if (existing) {
            if (existing.dataset.loaded === 'true') return resolve();
            // `{ once: true }` so repeated retries through the cached-rejected
            // promise reset path don't stack handlers on the same tag.
            existing.addEventListener('load', () => resolve(), { once: true });
            existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
            // Defensive timeout: if neither event fires within 30s, give up
            // so a stale tag doesn't pin the loader promise forever.
            setTimeout(() => {
                if (isAlreadyReady()) resolve();
                else reject(new Error(`Timed out waiting for existing script: ${src}`));
            }, 30_000);
            return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.defer = true;
        s.addEventListener(
            'load',
            () => {
                s.dataset.loaded = 'true';
                resolve();
            },
            { once: true }
        );
        s.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
        document.head.appendChild(s);
    });
}

/** Wrap a promise factory so a rejection clears the cached slot — otherwise
 *  one transient load failure permanently disables the loader for the
 *  rest of the SPA session. */
function memoizeWithRetry(
    get: () => Promise<void> | null,
    set: (p: Promise<void> | null) => void,
    build: () => Promise<void>
): Promise<void> {
    const cached = get();
    if (cached) return cached;
    const fresh = build().catch((err) => {
        set(null);
        throw err;
    });
    set(fresh);
    return fresh;
}

function loadGoogleIdentity(): Promise<void> {
    return memoizeWithRetry(
        () => _gisPromise,
        (p) => { _gisPromise = p; },
        () =>
            loadScript(GIS_SRC, () => !!window.google?.accounts).then(() => {
                // GIS appends itself to `window.google.accounts` synchronously after load.
                if (!window.google?.accounts) {
                    throw new Error('Google Identity Services loaded but window.google.accounts is missing');
                }
            })
    );
}

function loadPicker(): Promise<void> {
    return memoizeWithRetry(
        () => _pickerPromise,
        (p) => { _pickerPromise = p; },
        () =>
            loadScript(GAPI_SRC, () => !!window.gapi).then(
                () =>
                    new Promise<void>((resolve, reject) => {
                        if (!window.gapi) {
                            reject(new Error('gapi loaded but window.gapi is missing'));
                            return;
                        }
                        if (window.google?.picker) {
                            resolve();
                            return;
                        }
                        window.gapi.load('picker', () => {
                            if (!window.google?.picker) {
                                reject(new Error('picker module loaded but window.google.picker is missing'));
                                return;
                            }
                            resolve();
                        });
                    })
            )
    );
}

export interface PickedFolder {
    id: string;
    name: string;
    driveName?: string;
    url?: string;
}

/**
 * Warm up the Google Picker dependencies. Call this when the admin
 * navigates to the Drive flow so the click on "Pick from Google Drive"
 * can launch OAuth immediately — synchronously enough that browsers'
 * popup blockers still treat the auth window as user-initiated. Without
 * preload, the async script load between click and OAuth request can
 * cross the "lost user gesture" threshold.
 *
 * Returns silently on success; throws on transport / load failures so
 * callers can surface diagnostics if they want, but most callers just
 * fire-and-forget.
 */
export async function preloadGooglePicker(): Promise<void> {
    await Promise.all([loadGoogleIdentity(), loadPicker()]);
}

/**
 * Open the Google Drive folder picker. Resolves with the picked folder, or
 * `null` if the user cancelled.
 *
 * The OAuth grant is in-memory only — the returned access token is used
 * once to power the picker and then discarded.
 */
export async function pickDriveFolder(config: PickerConfig): Promise<PickedFolder | null> {
    await Promise.all([loadGoogleIdentity(), loadPicker()]);
    const gis = window.google?.accounts;
    const picker = window.google?.picker;
    if (!gis || !picker) {
        throw new Error('Google Picker dependencies failed to initialize');
    }

    // GIS error types that aren't really "errors" — they're the user
    // closing the popup or declining the grant. Treat them as a cancel so
    // the admin doesn't see a red banner for choosing "Cancel". Anything
    // else genuinely failed and should surface — in particular
    // `popup_failed_to_open` (browser popup blocker) is a real failure
    // that admins need to see, not a silent no-op.
    const CANCEL_TYPES = new Set(['popup_closed', 'user_cancel']);
    const CANCEL_ERRORS = new Set(['access_denied', 'interaction_required']);
    const CANCEL_SENTINEL = '__docreview_cancel__';

    let token: string;
    try {
        token = await new Promise<string>((resolve, reject) => {
            const client = gis.oauth2.initTokenClient({
                client_id: config.clientId,
                scope: DRIVE_SCOPE,
                callback: (resp) => {
                    if (resp.access_token) {
                        resolve(resp.access_token);
                    } else if (resp.error && CANCEL_ERRORS.has(resp.error)) {
                        reject(new Error(CANCEL_SENTINEL));
                    } else {
                        reject(new Error(resp.error ?? 'Google sign-in failed'));
                    }
                },
                error_callback: (err) => {
                    if (err.type && CANCEL_TYPES.has(err.type)) {
                        reject(new Error(CANCEL_SENTINEL));
                    } else if (err.type === 'popup_failed_to_open') {
                        reject(new Error('Browser blocked the Google sign-in popup. Allow popups for this site and retry.'));
                    } else {
                        reject(new Error(err.message ?? err.type ?? 'Google sign-in failed'));
                    }
                }
            });
            // `prompt: ''` reuses an existing grant when present; first run
            // shows the consent screen.
            client.requestAccessToken({ prompt: '' });
        });
    } catch (err) {
        if (err instanceof Error && err.message === CANCEL_SENTINEL) return null;
        throw err;
    }

    return new Promise<PickedFolder | null>((resolve, reject) => {
        // Watchdog: a misbehaving browser extension / CSP / network
        // hiccup can leave Picker in a non-terminal state where it
        // never fires `PICKED`/`CANCEL`/`ERROR`. Without this fallback
        // the modal's "Opening Drive…" spinner would stick forever.
        const PICKER_TIMEOUT_MS = 5 * 60 * 1000;
        const watchdog = setTimeout(() => {
            reject(new Error('Google Picker did not return a selection within 5 minutes. Reopen the modal to retry, or paste a folder ID directly.'));
        }, PICKER_TIMEOUT_MS);
        const safeResolve = (val: PickedFolder | null) => { clearTimeout(watchdog); resolve(val); };
        const safeReject = (err: unknown) => { clearTimeout(watchdog); reject(err); };
        try {
            // Picker shows one tab per `addView` call. Per Google's
            // samples, the documented way to surface BOTH My Drive
            // and Shared Drives is two DocsView instances:
            //  - A default DocsView (no `setEnableDrives`) for My Drive.
            //  - A second DocsView with `setEnableDrives(true)` for the
            //    Shared Drives the signed-in user is a member of.
            // `Feature.SUPPORT_DRIVES` on the builder makes the returned
            // metadata include the parent drive's name so we can show
            // the admin which drive they picked from.
            const myDriveView = new picker.DocsView(picker.ViewId.FOLDERS)
                .setSelectFolderEnabled(true)
                .setMimeTypes('application/vnd.google-apps.folder')
                .setIncludeFolders(true)
                .setMode(picker.DocsViewMode.LIST);

            const sharedDrivesView = new picker.DocsView(picker.ViewId.FOLDERS)
                .setSelectFolderEnabled(true)
                .setMimeTypes('application/vnd.google-apps.folder')
                .setIncludeFolders(true)
                .setMode(picker.DocsViewMode.LIST)
                .setEnableDrives(true);

            const built = new picker.PickerBuilder()
                .addView(myDriveView)
                .addView(sharedDrivesView)
                .enableFeature(picker.Feature.SUPPORT_DRIVES)
                .setOAuthToken(token)
                .setDeveloperKey(config.apiKey)
                .setOrigin(window.location.origin)
                .setTitle('Pick a Drive folder to index')
                .setCallback((data) => {
                    if (data.action === picker.Action.CANCEL) {
                        safeResolve(null);
                        return;
                    }
                    if (data.action === picker.Action.PICKED) {
                        const doc = data.docs?.[0];
                        if (!doc) {
                            safeResolve(null);
                            return;
                        }
                        safeResolve({
                            id: doc.id,
                            name: doc.name,
                            driveName: doc.driveName,
                            url: doc.url
                        });
                        return;
                    }
                    // Google Picker emits `Action.ERROR` on configuration
                    // issues (bad API key, missing scopes, drives feature
                    // disabled, etc.).
                    if (picker.Action.ERROR && data.action === picker.Action.ERROR) {
                        safeReject(new Error('Google Picker reported an error. Check the API key restrictions and OAuth client origins.'));
                    }
                })
                .build();
            built.setVisible(true);
        } catch (err) {
            safeReject(err);
        }
    });
}
